import { forwardRef, memo, useEffect, useRef, useState } from 'react'
import Select, { components, InputProps, OptionProps } from 'react-select'
import { Checkbox, Typography } from 'src/modules/core/components'
import classes from './select-field.module.scss'

type SelectFieldVariant = 'normal' | 'small'

interface SelectFieldProps {
  variant?: SelectFieldVariant
  [key: string]: any
}

function getCustomStyles(variant: SelectFieldVariant = 'normal') {
  return {
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? '#767676ff' : 'var(--bk-control, #5777DA)',
      transition: 'all 0.3s',
      transform: variant === 'small' ? 'scale(1.1)' : 'scale(1.3)',
      display: 'flex',
      '&:active': {
        color: 'var(--bk-control, #5777DA)',
      },
      '&:hover': {
        color: 'var(--bk-control, #5777DA)',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      padding: '3px 0',
      color: '#959595',
    }),
    option: (provided, state) => ({
      ...provided,
      borderBottom: '1px solid #E3E3E3',
      padding: variant === 'small' ? '12px 8px' : '16px 12px',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: state.isDisabled
        ? '#f5f5f5' // Light gray background for disabled options
        : state.isSelected
          ? 'var(--bk-control, #5777DA)' // Blue background for selected option
          : state.isFocused
            ? 'var(--bk-tint, #DEEBFF)'
            : '#fff',
      color: state.isSelected ? '#fff' : 'inherit', // White text for selected option
      '&:active': {
        backgroundColor: state.isFocused ? 'var(--bk-tint-strong, #B2D4FF)' : null,
      },
    }),
    control: (provided, state) => ({
      ...provided,
      border: state.isDisabled ? '1px solid #767676ff' : '1px solid var(--bk-control, #5777DA)',
      backgroundColor: state.isDisabled ? 'rgb(239 239 239 / 30%)' : 'white',
      outline: state.isFocused || state.isActive ? '1px solid var(--bk-control, #5777DA)' : '1px solid transparent',
      transition: 'outline 0.3s',
      boxShadow: 'none',
      borderRadius: '5px',
      padding: variant === 'small' ? '0' : '10px',
      fontSize: variant === 'small' ? '16px' : '20px',
      '&:hover': {
        border: '1px solid var(--bk-control, #5777DA)',
      },
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      borderRadius: '5px',
      border: '1px solid var(--bk-control, #5777DA)',
      boxSizing: 'border-box',
      boxShadow: 'none',
    }),
    menu: (provided) => ({
      ...provided,
      boxShadow: 'none',
    }),
  }
}

function TextOption(props: OptionProps & { variant?: SelectFieldVariant }) {
  return (
    <components.Option {...props}>
      <Typography
        variant="body"
        style={{
          color: 'inherit',
          fontSize: props.variant === 'small' ? '14px' : undefined,
        }}
      >
        {props.label}
      </Typography>
    </components.Option>
  )
}

function CheckboxOption(props: OptionProps & { variant?: SelectFieldVariant }) {
  return (
    <div>
      <components.Option {...props}>
        <Checkbox checked={props.isSelected} onChange={() => null} disabled labelDisabled={false} />
        <label className={classes.label}>
          <Typography
            variant="body"
            style={{
              fontSize: props.variant === 'small' ? '14px' : undefined,
            }}
          >
            {props.label}
          </Typography>
        </label>
      </components.Option>
    </div>
  )
}

// react-select's non-searchable DummyInput cannot be replaced and overwrites `aria-describedby`,
// so a caller's ids get a searchable but read-only Input that appends them to react-select's own.
function DescribedByInput(props: InputProps) {
  const callerDescribedBy = (props.selectProps as { callerDescribedBy?: string }).callerDescribedBy
  return (
    <components.Input
      {...props}
      aria-describedby={[props['aria-describedby'], callerDescribedBy].filter(Boolean).join(' ')}
      aria-readonly={true}
      readOnly
      onChange={() => undefined}
      inputMode="none"
      inputClassName={classes.readOnlyInput}
    />
  )
}

// eslint-disable-next-line react/display-name
export const SelectField = memo(
  forwardRef<any, SelectFieldProps>(({ variant = 'normal', 'aria-describedby': ariaDescribedBy, ...props }, ref) => {
    const menuCloseTimeRef = useRef<number | null>(null)
    const CLICK_WINDOW_MS = 50 // Only stop propagation for clicks within 50ms of menu close

    useEffect(() => {
      const handleDocumentClick = (e: MouseEvent) => {
        // Only stop propagation if the click happens within a short window after menu close
        // This prevents the click that closes the menu from bubbling, but allows
        // subsequent user clicks to work normally
        if (menuCloseTimeRef.current !== null) {
          const timeSinceMenuClose = Date.now() - menuCloseTimeRef.current
          if (timeSinceMenuClose < CLICK_WINDOW_MS) {
            e.stopPropagation()
            menuCloseTimeRef.current = null
          } else {
            // Window expired, clear the ref
            menuCloseTimeRef.current = null
          }
        }
      }
      document.addEventListener('click', handleDocumentClick, true)
      return () => document.removeEventListener('click', handleDocumentClick, true)
    }, [])

    const customStyles = getCustomStyles(variant)

    return (
      <Select
        isClearable={false}
        styles={customStyles}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        onMenuClose={() => {
          // Record the time when menu closes
          // This allows us to only stop propagation for clicks that happen
          // immediately during the closing animation, not subsequent user clicks
          menuCloseTimeRef.current = Date.now()
        }}
        isSearchable={!!ariaDescribedBy}
        ref={ref}
        components={{
          Option: (optionProps) =>
            props.isMulti ? (
              <CheckboxOption {...optionProps} variant={variant} />
            ) : (
              <TextOption {...optionProps} variant={variant} />
            ),
          IndicatorSeparator: () => null,
          ...(ariaDescribedBy ? { Input: DescribedByInput } : {}),
        }}
        {...(ariaDescribedBy ? { callerDescribedBy: ariaDescribedBy } : {})}
        {...props}
      />
    )
  }),
)
